import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { AddressAutocomplete } from '../AddressAutocomplete'
import { Button } from '../ui/Button'
import type { StructuredAddress } from '../../lib/mapbox'

const SITE_TYPES = ['Residential', 'Commercial', 'Industrial', 'Multi-Family', 'Religious', 'Educational', 'Government', 'Healthcare']

const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 } as const
const inputStyle = { padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 } as const

interface ClientOption {
  id: string
  company_name: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onCreated: (site: { slug: string }) => void
  /** Pre-set client — hides the client picker */
  clientId?: string
}

export function SiteCreateModal({ isOpen, onClose, onCreated, clientId }: Props) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState<Partial<StructuredAddress>>({})
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [type, setType] = useState('Residential')
  const [notes, setNotes] = useState('')
  const [selectedClientId, setSelectedClientId] = useState(clientId || '')
  const [clients, setClients] = useState<ClientOption[]>([])
  const [saving, setSaving] = useState(false)

  // Load clients when modal opens (only if no fixed clientId)
  useEffect(() => {
    if (!isOpen) return
    // Reset form
    setName('')
    setAddress({})
    setCity('')
    setState('')
    setZip('')
    setType('Residential')
    setNotes('')
    setSelectedClientId(clientId || '')

    if (!clientId) {
      api.get<{ data: ClientOption[] }>('/clients?limit=200')
        .then(r => setClients(r.data || []))
        .catch(() => setClients([]))
    }
  }, [isOpen, clientId])

  // Sync address autocomplete → individual fields
  const handleAddressChange = (addr: StructuredAddress) => {
    setAddress(addr)
    setCity(addr.city || '')
    setState(addr.state || '')
    setZip(addr.zip_code || '')
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const effectiveClientId = clientId || selectedClientId
      const endpoint = effectiveClientId
        ? `/clients/${effectiveClientId}/sites`
        : '/sites'

      const payload: Record<string, unknown> = {
        name,
        address: address.address_line1 || '',
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
        lat: address.latitude ?? undefined,
        lng: address.longitude ?? undefined,
        notes: type !== 'Residential'
          ? `Type: ${type}${notes ? '\n' + notes : ''}`
          : notes || undefined,
        ...(effectiveClientId ? { client_id: effectiveClientId } : {}),
      }

      const result = await api.post<{ slug?: string }>(endpoint, payload)
      onCreated({ slug: result?.slug || '' })
      onClose()
    } catch {
      // caller handles toast
    } finally {
      setSaving(false)
    }
  }

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="card" style={{ width: 480, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>New Site</h2>
        <div className="space-y-3">
          <div>
            <label style={labelStyle}>Site Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Goldstein Residence"
              className="v3-input w-full"
              style={inputStyle}
              autoFocus
            />
          </div>
          <div>
            <label style={labelStyle}>Address</label>
            <AddressAutocomplete
              value={address}
              onChange={handleAddressChange}
              placeholder="Start typing an address..."
              showMapPreview
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label style={labelStyle}>City</label>
              <input value={city} onChange={e => setCity(e.target.value)} className="v3-input w-full" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input value={state} onChange={e => setState(e.target.value)} className="v3-input w-full" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Zip</label>
              <input value={zip} onChange={e => setZip(e.target.value)} className="v3-input w-full" style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <div className="v3-select-wrap">
              <select value={type} onChange={e => setType(e.target.value)} className="v3-select w-full" style={inputStyle}>
                {SITE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="v3-select-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </span>
            </div>
          </div>
          {/* Client picker — only when no fixed clientId */}
          {!clientId && (
            <div>
              <label style={labelStyle}>Client (optional)</label>
              <div className="v3-select-wrap">
                <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} className="v3-select w-full" style={inputStyle}>
                  <option value="">No client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <span className="v3-select-chevron">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9" />
                  </svg>
                </span>
              </div>
            </div>
          )}
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="v3-textarea w-full" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        </div>
        <div className="flex justify-end gap-2" style={{ marginTop: 20 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!name.trim()} loading={saving}>
            Create Site
          </Button>
        </div>
      </div>
    </div>
  )
}
